import React, { useState, useEffect } from 'react';
import { FaVolumeUp, FaVolumeDown, FaVolumeMute, FaPlay } from 'react-icons/fa';
import soundManager from '../utils/soundManager';

const SoundSettings = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    isEnabled: true,
    volume: 0.7
  });

  useEffect(() => {
    // Load current settings
    const currentSettings = soundManager.getSettings();
    setSettings(currentSettings);
  }, []);

  const handleEnabledChange = (enabled) => {
    soundManager.setEnabled(enabled);
    setSettings(prev => ({ ...prev, isEnabled: enabled }));
  };

  const handleVolumeChange = (volume) => {
    soundManager.setVolume(volume);
    setSettings(prev => ({ ...prev, volume }));
  };

  const testSound = (soundType) => {
    soundManager.testSound(soundType);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">🔊 Sound Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Enable/Disable Sounds */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-700">Enable Sound Notifications</h3>
              <p className="text-sm text-gray-500">Play sounds for actions and notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.isEnabled}
                onChange={(e) => handleEnabledChange(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Volume Control */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">Volume</h3>
              <span className="text-sm text-gray-500">{Math.round(settings.volume * 100)}%</span>
            </div>
            <div className="flex items-center space-x-3">
              <FaVolumeMute className="text-gray-400" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                disabled={!settings.isEnabled}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
              />
              <FaVolumeUp className="text-gray-600" />
            </div>
          </div>

          {/* Test Sounds */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700">Test Sounds</h3>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => testSound('success')}
                disabled={!settings.isEnabled}
                className="flex flex-col items-center p-3 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaPlay className="text-green-600 mb-1" />
                <span className="text-xs text-green-700 font-medium">Success</span>
              </button>
              <button
                onClick={() => testSound('error')}
                disabled={!settings.isEnabled}
                className="flex flex-col items-center p-3 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaPlay className="text-red-600 mb-1" />
                <span className="text-xs text-red-700 font-medium">Error</span>
              </button>
              <button
                onClick={() => testSound('warning')}
                disabled={!settings.isEnabled}
                className="flex flex-col items-center p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaPlay className="text-yellow-600 mb-1" />
                <span className="text-xs text-yellow-700 font-medium">Warning</span>
              </button>
            </div>
          </div>

          {/* Sound Status Info */}
          <div className={`p-4 rounded-lg border ${
            soundManager.useBeepFallback 
              ? 'bg-yellow-50 border-yellow-200' 
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-start">
              <div className={`rounded-full p-1 mr-3 mt-0.5 ${
                soundManager.useBeepFallback 
                  ? 'bg-yellow-100' 
                  : 'bg-green-100'
              }`}>
                <svg className={`w-4 h-4 ${
                  soundManager.useBeepFallback 
                    ? 'text-yellow-600' 
                    : 'text-green-600'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className={`text-sm font-semibold mb-1 ${
                  soundManager.useBeepFallback 
                    ? 'text-yellow-800' 
                    : 'text-green-800'
                }`}>
                  {soundManager.useBeepFallback ? '🔊 Using Beep Sounds' : '🎵 Using MP3 Files'}
                </h4>
                <p className={`text-xs ${
                  soundManager.useBeepFallback 
                    ? 'text-yellow-700' 
                    : 'text-green-700'
                }`}>
                  {soundManager.useBeepFallback 
                    ? 'MP3 files not found. Using generated beep sounds as fallback. Add success.mp3, error.mp3, and warning.mp3 to public folder for custom sounds.' 
                    : 'All sound files loaded successfully! Custom MP3 sounds are active.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoundSettings;
